// Shared rule-engine primitives.
//
// Architecture rule: rules are *data*, versioned per financial year, kept
// in git, and unit-tested against canonical worked examples sourced from
// ATO / APRA / Services Australia. The calculation functions are pure —
// `inputs -> rules -> outputs`. No I/O, no Date.now(), no env reads. The
// assessment year is always passed in so historical re-runs (e.g. an
// FY2024-25 CCS reclaim) resolve against the right ruleset.

export const PRODUCTS = ["AU08", "AU06", "AU13"] as const;
export type Product = (typeof PRODUCTS)[number];

/** Financial-year key, e.g. "2025-26". */
export type FinancialYear = `${number}-${number}`;

/** Provenance + audit metadata attached to every versioned ruleset. */
export interface RuleSetMeta {
  product: Product;
  fy: FinancialYear;
  /** Inclusive start of the FY this ruleset governs (AU FY starts 1 July). */
  effectiveFrom: string;
  /** Inclusive end of the FY this ruleset governs (30 June). */
  effectiveTo: string;
  /** Source-of-truth URLs the figures were transcribed from. */
  sources: Record<string, string>;
  /** Date the source pages were last verified, ISO. */
  sourcesVerifiedAt: string;
}

/** A marginal/threshold band: `[from, to]` inclusive-exclusive on `to`. */
export interface Band {
  /** Lower bound, inclusive. */
  from: number;
  /** Upper bound, exclusive. `null` = unbounded (top band). */
  to: number | null;
  /** Rate applied within this band (fraction, e.g. 0.015 = 1.5%). */
  rate: number;
}

/** Pick the band whose `[from, to)` contains `amount`. */
export function bandFor(bands: readonly Band[], amount: number): Band {
  for (const band of bands) {
    if (amount >= band.from && (band.to === null || amount < band.to)) {
      return band;
    }
  }
  // Bands are authored to cover [0, ∞); a miss means a malformed ruleset.
  throw new Error(`no band matched amount ${amount}`);
}
